
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCandidateProfileThunk,
  updateCandidateProfileThunk,
  uploadCandidateProfileVideoThunk,
  uploadCandidateProfileResumeThunk,
} from "./candidateProfile.thunk";
import type { CandidateProfileState } from "../types/candidateProfile.types";

interface ExtendedCandidateProfileState extends CandidateProfileState {
  videoUploading: boolean;
  videoUploadError: string | null;
  resumeUploading: boolean;
  resumeUploadError: string | null;
}

const initialState: ExtendedCandidateProfileState = {
  data: null,
  loading: false,
  error: null,
  videoUploading: false,
  videoUploadError: null,
  resumeUploading: false,
  resumeUploadError: null,
};

const candidateProfileSlice = createSlice({
  name: "candidateProfile",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch profile
      .addCase(fetchCandidateProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidateProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchCandidateProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to fetch profile.";
      })

      // Update profile
      .addCase(updateCandidateProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCandidateProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(updateCandidateProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to update profile.";
      })

      // Upload or replace permanent profile video
      .addCase(uploadCandidateProfileVideoThunk.pending, (state) => {
        state.videoUploading = true;
        state.videoUploadError = null;
      })
      .addCase(
        uploadCandidateProfileVideoThunk.fulfilled,
        (state, action) => {
          state.videoUploading = false;
          state.videoUploadError = null;

          if (state.data) {
            state.data.video = action.payload;
          }
        }
      )
      .addCase(
        uploadCandidateProfileVideoThunk.rejected,
        (state, action) => {
          state.videoUploading = false;
          state.videoUploadError =
            action.payload ||
            "Failed to upload introduction video.";
        }
      )

      // Upload or replace permanent profile resume
      .addCase(uploadCandidateProfileResumeThunk.pending, (state) => {
        state.resumeUploading = true;
        state.resumeUploadError = null;
      })
      .addCase(
        uploadCandidateProfileResumeThunk.fulfilled,
        (state, action) => {
          state.resumeUploading = false;
          state.resumeUploadError = null;

          if (state.data) {
            state.data.resume = action.payload;
          }
        }
      )
      .addCase(
        uploadCandidateProfileResumeThunk.rejected,
        (state, action) => {
          state.resumeUploading = false;
          state.resumeUploadError =
            action.payload || "Failed to upload resume.";
        }
      );
  },
});

export default candidateProfileSlice.reducer;
